/**
 * REVCLOUD draft-flow helpers (REVCLOUD-MODES slice).
 *
 * AutoCAD REVCLOUD opens with
 *   "Specify first point or [Arc length/Object/Rectangular/Polygonal/
 *    Freehand/Style/Modify] <Object>:"
 * and the chosen creation method / arc length / style PERSIST across
 * invocations (Autodesk: REVCLOUDCREATEMODE, REVCLOUDAPPROXARCLEN sysvars
 * + per-drawing style). The sysvars themselves are owned by the
 * REVCLOUD-SYSVARS slice — until it lands, this module keeps the
 * session-lifetime defaults the state machine consumes; when the sysvar
 * schema gains REVCLOUDCREATEMODE / REVCLOUDARCVARIANCE these getters
 * become the single consumption point to re-route.
 *
 * Also hosts the Freehand hover sampler: AutoCAD's Freehand method draws
 * the cloud as the crosshairs are GUIDED along the path (no clicks) and
 * closes automatically when the cursor returns to the start point. The
 * stage feeds hover points through `useDraftSession.handleStageHover`,
 * which calls `advanceRevcloudFreehandHover` while the freehand step is
 * active.
 */

import { t } from "../../i18n";
import type {
  AdvanceResult,
  DraftPoint,
  DraftSession,
} from "../draftTypes";
import { distanceBetween } from "../draftTypes";

// ── Persistent defaults (REVCLOUD-SYSVARS consumption point) ─────────

export type RevcloudCreateMode =
  | "Rectangular"
  | "Polygonal"
  | "Freehand"
  | "Object";

export type RevcloudStyle = "Normal" | "Calligraphy";

type RevcloudDefaults = {
  mode: RevcloudCreateMode;
  arcChordLength: number;
  style: RevcloudStyle;
};

// AutoCAD defaults: REVCLOUDCREATEMODE = 1 (Rectangular); arc length
// default mirrors the existing repo-wide create-revcloud contract
// default (1); style Normal.
const defaults: RevcloudDefaults = {
  mode: "Rectangular",
  arcChordLength: 1,
  style: "Normal",
};

export function getRevcloudDefaults(): Readonly<RevcloudDefaults> {
  return defaults;
}

export function setRevcloudDefaultMode(mode: RevcloudCreateMode): void {
  defaults.mode = mode;
}

export function setRevcloudDefaultArcLength(length: number): void {
  if (Number.isFinite(length) && length > 0) defaults.arcChordLength = length;
}

export function setRevcloudDefaultStyle(style: RevcloudStyle): void {
  defaults.style = style;
}

/** Test hook — reset the persisted defaults to the AutoCAD start state. */
export function resetRevcloudDefaultsForTest(): void {
  defaults.mode = "Rectangular";
  defaults.arcChordLength = 1;
  defaults.style = "Normal";
}

// ── Session readers ──────────────────────────────────────────────────

/** Effective creation mode for a session: explicit option → persisted. */
export function revcloudSessionMode(session: DraftSession): RevcloudCreateMode {
  const raw = session.options.revcloudMode;
  if (
    raw === "Rectangular" ||
    raw === "Polygonal" ||
    raw === "Freehand" ||
    raw === "Object"
  ) {
    return raw;
  }
  return defaults.mode;
}

/** Effective arc chord length: explicit option → persisted. */
export function revcloudSessionArcLength(session: DraftSession): number {
  const raw = Number(session.options.revcloudArcLength);
  return Number.isFinite(raw) && raw > 0 ? raw : defaults.arcChordLength;
}

/** Effective cloud style: explicit option → persisted. */
export function revcloudSessionStyle(session: DraftSession): RevcloudStyle {
  const raw = session.options.revcloudStyle;
  return raw === "Calligraphy" ? "Calligraphy" : raw === "Normal" ? "Normal" : defaults.style;
}

// ── Keyword matching ─────────────────────────────────────────────────
//
// EN tokens follow the Autodesk REVCLOUD prompt capitals (A/O/R/P/F/S/M);
// RU tokens mirror the RU AutoCAD prompt "[Длина дуги/Объект/
// Прямоугольное/Полигональное/От руки/Стиль/Изменить]" with unambiguous
// short forms (ПР vs ПОЛ — both options start with "П", so the
// single-letter form is not offered for either, matching the repo's
// polygon/hatch keyword convention of exact-token sets).

export type RevcloudFirstPromptKeyword =
  | "arc-length"
  | "object"
  | "rectangular"
  | "polygonal"
  | "freehand"
  | "style"
  | "modify";

const FIRST_PROMPT_KEYWORDS: ReadonlyArray<
  [RevcloudFirstPromptKeyword, ReadonlySet<string>]
> = [
  ["arc-length", new Set(["A", "ARC", "ARC LENGTH", "ARCLENGTH", "Д", "ДЛИНА", "ДЛИНА ДУГИ"])],
  ["object", new Set(["O", "OBJECT", "О", "ОБЪЕКТ"])],
  ["rectangular", new Set(["R", "RECT", "RECTANGULAR", "ПР", "ПРЯМОУГОЛЬНОЕ", "ПРЯМОУГОЛЬНАЯ"])],
  ["polygonal", new Set(["P", "POLY", "POLYGONAL", "ПОЛ", "ПОЛИГОНАЛЬНОЕ", "ПОЛИГОНАЛЬНАЯ"])],
  ["freehand", new Set(["F", "FREEHAND", "Р", "РУКИ", "ОТ РУКИ"])],
  ["style", new Set(["S", "STYLE", "С", "СТИЛЬ"])],
  ["modify", new Set(["M", "MODIFY", "И", "ИЗМЕНИТЬ"])],
];

export function matchRevcloudFirstPromptKeyword(
  normalizedText: string,
): RevcloudFirstPromptKeyword | null {
  for (const [keyword, tokens] of FIRST_PROMPT_KEYWORDS) {
    if (tokens.has(normalizedText)) return keyword;
  }
  return null;
}

/** Style sub-prompt — "Select arc style [Normal/Calligraphy] <Normal>". */
export function matchRevcloudStyleKeyword(
  normalizedText: string,
): RevcloudStyle | null {
  if (["N", "NORMAL", "О", "ОБЫЧНЫЙ"].includes(normalizedText)) return "Normal";
  if (["C", "CALLIGRAPHY", "К", "КАЛЛИГРАФИЯ"].includes(normalizedText)) {
    return "Calligraphy";
  }
  return null;
}

/** Object-mode "Reverse direction [Yes/No] <No>" — null = not a keyword. */
export function matchRevcloudReverseKeyword(
  normalizedText: string,
): boolean | null {
  if (["Y", "YES", "Д", "ДА"].includes(normalizedText)) return true;
  if (normalizedText === "" || ["N", "NO", "Н", "НЕТ"].includes(normalizedText)) {
    return false;
  }
  return null;
}

// ── Freehand hover sampling ──────────────────────────────────────────

/**
 * Advance the Freehand path with a HOVER point. Appends a sample every
 * ~arcChordLength of cursor travel (each rendered cloud arc spans
 * roughly the configured arc length) and AUTO-CLOSES the cloud when the
 * cursor returns within one arc length of the start point after at
 * least 3 sampled vertices — AutoCAD's "the cloud closes automatically
 * when you return to the start point".
 *
 * Returns `null` when the hover changes nothing (caller keeps the
 * session untouched); a commit result ends the session with the
 * Freehand create-revcloud dispatch.
 */
export function advanceRevcloudFreehandHover(
  session: DraftSession,
  point: DraftPoint,
): AdvanceResult | null {
  if (session.step !== "revcloud-freehand-path") return null;
  const start = session.points[0];
  if (!start) return null;
  const arcLength = revcloudSessionArcLength(session);
  const last = session.points[session.points.length - 1] ?? start;

  if (
    session.points.length >= 3 &&
    distanceBetween(point, start) <= arcLength
  ) {
    return commitRevcloudFreehand(session, session.spaceId);
  }

  if (distanceBetween(point, last) < arcLength) {
    return null;
  }
  return {
    nextSession: {
      ...session,
      points: [...session.points, { x: point.x, y: point.y }],
      hoverPoint: { x: point.x, y: point.y },
    },
  };
}

/** Commit the sampled Freehand path (auto-close or explicit Enter). */
export function commitRevcloudFreehand(
  session: DraftSession,
  activeSpaceId: string,
): AdvanceResult {
  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/create-revcloud",
        source: "scene",
        spaceId: activeSpaceId,
        creationMode: "Freehand",
        vertices: session.points.map((p) => ({ x: p.x, y: p.y })),
        arcChordLength: revcloudSessionArcLength(session),
        revisionCloudStyle: revcloudSessionStyle(session),
        reverseDirection: false,
      },
      summary: t("summary.revcloud_created"),
      commandText: "REVCLOUD",
    },
  };
}
